import { AbstractSceneTransition } from "slotworks/graphics/layers/scene-transitions/abstract-scene-transition";
import Contract from "slotworks/utils/contract";
import * as particles from "pixi-particles";
import * as TweenJS from "slotworks/utils/tween";
import {EndlessHover, BounceZoomOut} from "slotworks/graphics/animation/base-animations";
import Layers from "slotworks/graphics/layers/layers";
import deviceInfo from "slotworks/utils/device-info";
import Point from "slotworks/utils/geom/point";
import { sequence, parallel } from "slotworks/utils/race-utils";
import { CenterPivot, Ungroup } from "slotworks/graphics/pixi/group";
import GameState from "slotworks/model/game-state";
import betModel from "slotworks/model/bets/bet-model";
import Components from "slotworks/components/components";
import Timer from "slotworks/utils/timer";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import { Sprite } from "pixi.js";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";

export default class RevolverWinTransitions extends AbstractSceneTransition {
    
    public BOUNCE_TIME: number = 700;
    public HOVER_SCALE: number = 1.15;
    public static HOVER_TIME: number = 800;
    public BOTTOM_LINE_DELAY: number = 100;
    public EXPLODE_DURATION: number = 250;
    public TITLE_ZOOM_TWEEN = 1.2;

    protected static currentTiers: number;

    protected static coins: particles.Emitter;

    protected static glowStack: PIXI.Sprite[] = [];
    protected static glowContainer: PIXI.Container;
    protected static glowTweens: TweenJS.Tween[];

    protected static winValue: PIXI.extras.BitmapText;
    protected static winGlow: PIXI.Sprite;

    protected static winTextHover: TweenJS.Tween;
    protected static winValueTween: TweenJS.Tween;

    protected static background: PIXI.Sprite;

    protected timers: number[] = [];
    protected lettersTweens: TweenJS.Tween[] = [];

    protected inResolve: Function;    

    protected resolveIn() {
        if (this.inResolve) {
            this.inResolve();
        }

        this.inResolve = null;
    }

    protected zoomInStacks(time: number = 350, glowScale: number = 2.55, delay: number = 0, mid: number = 1.5) {
        RevolverWinTransitions.glowContainer = new PIXI.Container();
        Layers.CelebrationBackground.container.addChildAt(RevolverWinTransitions.glowContainer, 1);

        RevolverWinTransitions.glowContainer.position.set(
            RevolverWinTransitions.glowStack[0].x + RevolverWinTransitions.glowStack[0].width/2,
            RevolverWinTransitions.glowStack[0].y + RevolverWinTransitions.glowStack[0].height/2
        );
        for(let i = RevolverWinTransitions.glowStack.length - 1; i >= 0; i--) {
            RevolverWinTransitions.glowStack[i].anchor.set(0.5, 0.5);
            RevolverWinTransitions.glowStack[i].position.set(0, 0);
            RevolverWinTransitions.glowContainer.addChild(RevolverWinTransitions.glowStack[i]);
        }

        this.startGlowStack();
        RevolverWinTransitions.glowContainer.scale.set(0, 0);
        return new Contract(resolve => {
                new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale)
                .to({x: mid, y: mid}, time)
                .delay(delay)
                .chain(
                    new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale)
                    .to({ x: glowScale, y: glowScale}, time)
                    .onComplete(() => { resolve(null) }))            
                .start();
            });
    }

    protected zoomOutStacks(scale: number = 0) {
        return sequence([
            () => new Contract(resolve => {
                if(scale == 0) resolve(null);
                else {
                    new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale)
                    .to({x: scale, y: scale}, 150)
                    .onComplete(() => {                                        
                        resolve(null);                    
                    }).start();               
                }
            }),
            () => Contract.getTimeoutContract(200),
            () => new Contract(resolve => {
                new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale)
                .to({x: 0, y: 0}, 250)
                .onComplete(() => {
                    this.stopGlowStack();
                    RevolverWinTransitions.glowStack.forEach(element => {
                        element.x = RevolverWinTransitions.glowContainer.x;
                        element.y = RevolverWinTransitions.glowContainer.y;
                        Layers.CelebrationBackground.container.addChild(element);
                        element.visible = false;
                    });
                    resolve(null);
                }).start();                
            })
        ]);
    }

    protected scaleStacks(scale: number, time: number) {
        return new TweenJS.Tween(RevolverWinTransitions.glowContainer.scale)
                .to({x: scale, scale: 0}, time)
                .start();
    }

    protected stopGlowStack() {
        RevolverWinTransitions.glowTweens.forEach(tween => {
            tween.stop();
        });
        RevolverWinTransitions.glowTweens = [];
    }


    public zoom(target: PIXI.Container,
        scaleFrom: number, scaleTo: number, duration: number, easing: (k: number) => number,
        pivot: Point = null): Contract<void> {

        return new Contract<void>((resolve) => {

            const group = CenterPivot(target, null, pivot);

            group.scale.set(scaleFrom, scaleFrom);

            const tween = new TweenJS.Tween(target.scale)
                .to({x: scaleTo, y: scaleTo}, duration)
                .easing(easing);
            tween.onComplete(() => {
                tween.onComplete(null);
                Ungroup(group);
                target.scale.set(scaleTo, scaleTo);
                resolve(null);
            });
            tween.start();
        });
    }

    protected zoomTextIn(middleScale: number, endScale: number, delay: number = 0): Contract<void> {
        return new Contract<void>(resolve=>{
            const target = RevolverWinTransitions.winValue;
            target.visible = true;
            TweenJS.removeAll(target.scale);
            (target as any).anchor.y = 0;
            target.y -= target.height/2;
            target.scale.set(0, 0);
            new TweenJS.Tween(target.scale)
            .to({x: middleScale, y: middleScale}, 300)
            .delay(delay)
            .onComplete(()=>{                
                TweenJS.removeAll(target.scale);
                new TweenJS.Tween(target.scale)
                .to({x: endScale, y: endScale}, 300)
                .onComplete(()=>{                   
                    TweenJS.removeAll(target.scale);
                    RevolverWinTransitions.winValueTween = this.hover(target, 1.1, RevolverWinTransitions.HOVER_TIME, false);
                    resolve(null);                    
                }).start();                
            }).start();
        });
    }

    protected startGlowStack() {
        RevolverWinTransitions.glowStack[0].scale.set(0.6, 0.6);
        RevolverWinTransitions.glowStack[1].scale.set(2.7, 2.7);
        RevolverWinTransitions.glowStack[2].scale.set(2.7, 2.7);
        RevolverWinTransitions.glowStack[3].scale.set(2.7, 2.7);
        
        RevolverWinTransitions.glowTweens = [];
        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(RevolverWinTransitions.glowStack[0])
            .to({ rotation: Math.PI * 2 }, 40000)
            .repeat(Infinity)
            .start());
        
        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(RevolverWinTransitions.glowStack[1])
            .to({ rotation: Math.PI * 2 }, 25000)
            .repeat(Infinity)
            .start());
        
        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(RevolverWinTransitions.glowStack[2])
            .to({ rotation: Math.PI * 2 }, 30000)
            .repeat(Infinity)
            .start());
        RevolverWinTransitions.glowTweens.push(
            new TweenJS.Tween(RevolverWinTransitions.glowStack[3])
            .to({ rotation: Math.PI * 2 }, -25000)
            .repeat(Infinity)
            .start());
    }

    protected clearTimers() {
        if (this.timers) {
            for (const timer of this.timers) {
                Timer.clearTimeout(timer);
            }
            this.timers = [];
        }
    }

    protected zoomOut(sprite: PIXI.Sprite | PIXI.extras.BitmapText, duration: number): Contract<void> {
        if(sprite instanceof PIXI.Sprite &&sprite.anchor.x == 0)
            this.centerElement(sprite as any, 0.5, 0.5);
        return new Contract(resolve => {
            new TweenJS.Tween(sprite.scale)
            .to({x: 0, y: 0}, duration)
            .onComplete(resolve)
            .start();
        });
    }

    protected anchor(el: PIXI.Sprite, anchorX: number, anchorY: number) {
        el.anchor.set(anchorX, anchorY);
        el.x += el.width * anchorX;
        el.y += el.height * anchorY;
    }

    protected setLettersAnchors(letters: PIXI.Sprite[]) {        
        const midx = Layers.CelebrationContent.globalToLocal(new Point(1920/2, 0)).x;
        const midy = letters[0].y + letters[0].height * 0.7;
        for(let i = 0; i < letters.length; i++) {
            const letter = letters[i];
            const distanceX = midx - letter.x;
            const anchorX = distanceX / letter.width;
            const distanceY = midy - letter.y;
            const anchorY = distanceY / letter.height;
            letter.anchor.set(anchorX, anchorY);
            letter.position.set(letter.x + distanceX, letter.y + distanceY);
            letters[i].scale.set(0, 0);
        }
    }

    protected zoomInLetters(letters: PIXI.Sprite[], final: boolean = false, epic: boolean = false) {
        if(final) {
            for(let i = 0; i < letters.length; i++) {
                this.zoomInLetterWin(letters[i], 300, i * 80, i, epic ? 1.15 : 1.1, epic);
            }
        } else {
            for(let i = 0; i < letters.length; i++) {
                this.zoomInLetter(letters[i], 300, i * 80, i, 1.1);
            } 
        }
    }

    protected zoomInLetterWin(letter: PIXI.Sprite, time: number, delay: number, id: number, scale: number = 1, epic: boolean = false) {
        letter.scale.set(0, 0);
        this.lettersTweens.push(new TweenJS.Tween(letter.scale)
        .to({x: scale, y: scale}, time)
        .onComplete(() => {
            let tween:TweenJS.Tween = null;
            if(delay > 100) {
                tween = new TweenJS.Tween(letter.scale)
                .to({x: 1.05, y: 1.05}, 150)
                .repeat(5)
                .yoyo(true)
                .start();
                this.lettersTweens.push(tween);
            }
            this.timers.push(Timer.setTimeout(() => {
                if(tween) {
                    tween.stop();
                }
                if(epic) {
                    this.lettersTweens.push(new TweenJS.Tween(letter.scale)
                    .to({x: [1, 1.2, 1.1, 1.15, 1.1, 1.2, 0.82, 0.7, 0.85, 0.80, 0.86, 0.84, 0.88, 1.1, 1.2, 1.1, 1.05, 1.2, 1.1, 1.15, 1.05, 1.1, 0.9, 0.82, 0.84, 0.8, 0.7, 0.6, 0.7], 
                        y: [1, 1.2, 1.1, 1.15, 1.1, 1.2, 0.82, 0.7, 0.85, 0.80, 0.86, 0.84, 0.88, 1.1, 1.2, 1.1, 1.05, 1.2, 1.1, 1.15, 1.05, 1.1, 0.9, 0.82, 0.84, 0.8, 0.7, 0.6, 0.7]}, 6200)
                    .start());
                } else {
                    this.lettersTweens.push(new TweenJS.Tween(letter.scale)
                    .to({x: [1, 1.2, 1.1, 1.15, 1.1, 1.2, 0.82, 0.7, 0.85, 0.80, 1.1, 1.2, 1.1, 1.05, 1.2, 1.1, 1.15, 1.05, 1.1, 0.9, 0.82, 0.84, 0.8, 0.7, 0.6], 
                        y: [1, 1.2, 1.1, 1.15, 1.1, 1.2, 0.82, 0.7, 0.85, 0.80, 1.1, 1.2, 1.1, 1.05, 1.2, 1.1, 1.15, 1.05, 1.1, 0.9, 0.82, 0.84, 0.8, 0.7, 0.6]}, 5600)
                    .start());
                }
            }, delay));
        })
        .start());
    }

    protected zoomInLetter(letter: PIXI.Sprite, time: number, delay: number, id: number, scale: number = 1) {
        letter.scale.set(0, 0);
        this.lettersTweens.push(new TweenJS.Tween(letter.scale)
        .to({x: scale, y: scale}, time)
        .onComplete(() => {
            let tween:TweenJS.Tween = null;
            if(delay > 100) {
                tween = new TweenJS.Tween(letter.scale)
                .to({x: 1.05, y: 1.05}, 100)
                .repeat(5)
                .yoyo(true)
                .start();
                this.lettersTweens.push(tween);
            }
            this.timers.push(Timer.setTimeout(() => {
                if(tween) {
                    tween.stop();
                }
                this.lettersTweens.push(new TweenJS.Tween(letter.scale)
                .to({x: [0.98, 1.05, 1, 0.98, 1, 1.08, 1, 0.82, 0.75, 0.82, 0.78, 0.85, 0.75, 0.8, 0.76, 0.8, 0.76, 0.7, 0.72], 
                    y:  [0.98, 1.05, 1, 0.98, 1, 1.08, 1, 0.82, 0.75, 0.82, 0.78, 0.85, 0.75, 0.8, 0.76, 0.8, 0.76, 0.7, 0.72]}, 2800)
                .start());
            }, delay));
        })
        .start());
    }

    protected cleanLetterTweens() {
        for(let tween of this.lettersTweens) {
            if(tween) tween.stop();
        }
        this.lettersTweens.length = 0;
    }

    protected zoomOutLetters(letters: PIXI.Sprite[], time: number) {
        this.cleanLetterTweens();
        new TweenJS.Tween(letters[0].scale)
        .to({x: 0, y: 0}, time)
        .onUpdate(() => {
            for(let i = 1; i < letters.length; i++) {
                letters[i].scale.set(letters[0].scale.x, letters[0].scale.y)
            }
        })
        .onComplete(() => {
            for(let i = 0; i < letters.length; i++) 
                letters[i].visible = false;
        })
        .start();
    }

    protected cleanTextTween() {
        if(RevolverWinTransitions.winValueTween) {
            RevolverWinTransitions.winValueTween.stop();
            TweenJS.remove(RevolverWinTransitions.winValueTween);
            RevolverWinTransitions.winValueTween = null;
        }
    }

    protected emphasizeText(max:number, mid: number, min: number) {
        this.cleanTextTween();
        const target = RevolverWinTransitions.winValue;
        RevolverWinTransitions.winValueTween = new TweenJS.Tween(target.scale)
        .to({x: max, y: max}, 500)
        .onComplete(()=>{ 
            RevolverWinTransitions.winValueTween = new TweenJS.Tween(target.scale)
            .to({x: mid, y: mid}, 500)
            .onComplete(()=>{
                RevolverWinTransitions.winValueTween = this.hover(target, min, RevolverWinTransitions.HOVER_TIME, false);
            })
            .start();
        })
        .start();
    }

    protected scaleDownText() {
        this.cleanTextTween();
        RevolverWinTransitions.winValueTween = new TweenJS.Tween(RevolverWinTransitions.winValue.scale)
        .to({x: 0.85, y: 0.85}, 300)
        .start();
    }

    protected fadeIn(sprite: PIXI.Sprite, time: number, endAlpha: number) {
        sprite.alpha = 0;
        new TweenJS.Tween(sprite)
        .to({alpha: endAlpha}, time)
        .start();
    }

    protected hover(target: PIXI.Sprite | PIXI.extras.BitmapText, scale: number, duration: number, setInitialScale: boolean = true): TweenJS.Tween {        
        TweenJS.removeAll(target.scale);
        const tween = new TweenJS.Tween(target.scale)
        .to({x: scale, y: scale}, duration)
        .easing(TweenJS.Easing.Quadratic.InOut)
        .yoyo(true)
        .repeat(Infinity)
        .onUpdate(() => {
            if (!target) {
                TweenJS.remove(tween);
                return;
            }
        });

        if(setInitialScale) target.scale.x = target.scale.y = 1;    
        tween.start();

        return tween;
    }

    protected centerElement(sprite: PIXI.Sprite | Sprite, anchorX: number = 0, anchorY: number) {
        sprite.x -= sprite.width * sprite.anchor.x;
        sprite.y -= sprite.width * sprite.anchor.y;
        sprite.anchor.set(anchorX, anchorY);
        sprite.x += sprite.width * anchorX;
        sprite.y += sprite.height * anchorY;
    }

    protected resetEmitter(emitter: particles.Emitter) {
        emitter.emit = false;
        emitter.cleanup();
    }

    public getSelectedTiers(freespins: boolean = false): any[] {
        let winnings;
        if(freespins) {
            winnings = GameState.getCurrentGame().getRunningFreespinsWinTotal();
        } else if (GameState.getCurrentGame().getCurrentRecord().beenPaid || GameState.getCurrentGame().getCurrentRecord() instanceof FreespinRecord)  {
            winnings = GameState.getCurrentGame().totalCreditsWon;
        } else {
            winnings = GameState.getCurrentGame().getCurrentRecord().creditsWon;
        }
        
        const stake = betModel.getTotalBet();

        const multiplier = winnings / stake;

        const selectedLevels: any[] = [];
        let tiers = Components.config.bigWin.tiers;
        for (const level of tiers) {
            if (level.threshold > multiplier) {
                break;
            } else {
                selectedLevels.push(level);
            }
        }

        return selectedLevels;
    }

    protected updateBackground = () => {
        RevolverWinTransitions.background.width = canvasManager.stageWidth;            
        RevolverWinTransitions.background.x = -canvasManager.stageOffsetX;
        if(canvasManager.orientation == Orientation.PORTRAIT) {
            RevolverWinTransitions.background.height = canvasManager.stageHeight * 1.43;
            RevolverWinTransitions.background.y = -(canvasManager.stageOffsetY *2 - Layers.CelebrationBackground.container.y);
        } else {
            RevolverWinTransitions.background.height = canvasManager.stageHeight;
            RevolverWinTransitions.background.y = -canvasManager.stageOffsetY;
        }
    }

    protected clean() {
        if(!deviceInfo.isDesktop) {
            canvasManager.onOrientationChange.remove(this.updateBackground);
        }
    }

    protected resetEverything() {        
        RevolverWinTransitions.background = Layers.CelebrationBackground.getSprite("winrect");
        RevolverWinTransitions.background.visible = false;
        if(!deviceInfo.isDesktop) {
            this.updateBackground();
            canvasManager.onOrientationChange.add(this.updateBackground);
        }
        RevolverWinTransitions.coins = Layers.CelebrationBackground.getParticle("coins");
        RevolverWinTransitions.coins.emit = false;

        RevolverWinTransitions.winValue = Layers.CelebrationForeground.getBitmapText("value");
        RevolverWinTransitions.winGlow = Layers.CelebrationBackground.getSprite("glow");

        RevolverWinTransitions.glowStack = [];   /// big, mega, super, epic 
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite("shine"));  // 64, 100, 150, 230
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite("star2"));  // 256, 100, 600, 920
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite("star1"));  // 256, 100
        RevolverWinTransitions.glowStack.push(Layers.CelebrationBackground.getSprite("star0"));  // 256, 100       
    }

    
}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/transitions/revolver-win-transitions.ts